import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPaperPlane, faEdit, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogFooter
} from "../ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue
} from "../ui/select";
import Button from "../button-v1";
import styles from "../../styles/billing.module.css";
import { getProviders } from "../../services/case-manager/case-manager";
import { parseDateSafe } from "../../utils/dateFormatters";

// Helper function to format dates as MM-DD-YYYY HH:MM (24h format, unique to this component)
const formatUSADateTime = (dateInput, includeTime = true) => {
  if (!dateInput) return "—";
  const d = dateInput instanceof Date ? dateInput : parseDateSafe(dateInput);
  if (!d) return typeof dateInput === 'string' ? dateInput : "—";

  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const year = d.getFullYear();

  if (!includeTime) {
    return `${month}-${day}-${year}`;
  }

  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${month}-${day}-${year} ${hours}:${minutes}`;
};

// Helper to format age with edge case handling
const formatAge = (age) => {
  if (age === null || age === undefined || age === "") return "—";
  const numAge = Number(age);
  if (isNaN(numAge) || numAge < 0) return "—"; // Invalid or future DOB
  if (numAge === 0) return "< 1"; // Infant (less than 1 year old)
  return String(numAge);
};

// CPT Code descriptions
const CPT_DESCRIPTIONS = {
  "99453": "Initial setup & patient education for remote monitoring",
  "99454": "Device supply with daily data transmission (30-day period)",
  "99457": "First 20 minutes of RPM treatment management services",
  "99458": "Each additional 20 minutes of RPM treatment management",
  "99445": "Synchronous RPM telephone/telehealth consultation",
  "99470": "Remote physiologic monitoring setup",
  "G0506": "Comprehensive assessment & care planning for CCM",
  "99490": "First 20 minutes of clinical staff time for CCM",
  "99439": "Each additional 20 minutes of CCM services",
  "99487": "Complex CCM services, first 60 minutes",
  "99489": "Complex CCM services, each additional 30 minutes",
};

const AppealLetterEmailForm = ({
  isOpen,
  onClose,
  onSendEmail,
  patient,
  programType,
  billingPeriod,
  deniedCodes,
  isSending,
}) => {
  // Get patient data
  const patientName = patient?.patient_name || "";
  const facility = patient?.facility || "";
  const patientDOB = patient?.dob || patient?.date_of_birth || "";
  const patientAge = patient?.age || "";
  const patientGender = patient?.gender || "";
  const enrollmentDate = patient?.enrollment_date || patient?.created_at || "";
  const consentDate = patient?.consent_date || patient?.created_at || "";

  // Fetch providers
  const { data: providers = [], isLoading: isLoadingProviders } = useQuery(
    ["providers"],
    getProviders,
    { enabled: isOpen }
  );

  // Section collapse states - all open by default since they contain required fields
  const [sectionsOpen, setSectionsOpen] = useState({
    payer: true,
    patient: true,
    denial: true,
    service: true,
    careMgmt: true,
  });

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Form state - Payer Information
  const [payerInfo, setPayerInfo] = useState({
    payerName: "",
    payerAddress: "",
    payerCity: "",
    payerState: "",
    payerZip: "",
  });

  // Form state - Patient/Claim Information
  const [claimInfo, setClaimInfo] = useState({
    memberId: "",
    claimNumber: "",
  });

  // Form state - Denial Information
  const [denialInfo, setDenialInfo] = useState({
    eobDate: "",
    denialReason: "",
  });

  // Form state - Service Details
  const [serviceDetails, setServiceDetails] = useState({
    daysOfReadings: "",
    totalTimeMinutes: "",
    keyDataPoints: "",
    medicalNecessity: "",
  });

  // Form state - Care Management
  const [careMgmt, setCareMgmt] = useState({
    carePlanDate: "",
    goalsSummary: "",
    interventionsSummary: "",
    clinicalNotesRange: "",
  });

  // Provider/Signature state
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerTitle, setProviderTitle] = useState("");
  const [credentials, setCredentials] = useState("");
  const [npi, setNpi] = useState("");
  const [practiceName, setPracticeName] = useState(facility);
  const [providerPhone, setProviderPhone] = useState("");
  const [providerEmail, setProviderEmail] = useState("");
  const [practiceTaxId, setPracticeTaxId] = useState("");

  // Email recipient
  const [toEmail, setToEmail] = useState("");

  const [letterDate, setLetterDate] = useState(() => {
    return formatUSADateTime(new Date(), true);
  });

  const [isEditing, setIsEditing] = useState(false);

  // Update fields when provider is selected
  useEffect(() => {
    if (selectedProviderId && providers.length > 0) {
      const provider = providers.find(p => p.id === selectedProviderId || p.staff_id === selectedProviderId);
      if (provider) {
        setProviderName(provider.name || provider.staff_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim());
        setCredentials(provider.credentials || provider.title || "");
        setNpi(provider.npi || "");
        setProviderPhone(provider.phone || "");
        setProviderEmail(provider.email || "");
      }
    }
  }, [selectedProviderId, providers]);

  // Update practice name when facility changes
  useEffect(() => {
    if (facility) setPracticeName(facility);
  }, [facility]);

  // Get ICD codes - combine all ICD-10 codes from both RPM and CCM programs
  const icdCodes = useMemo(() => {
    const rpmCodes = patient?.rpm_codes || [];
    const ccmCodes = patient?.ccm_codes || patient?.icd_codes || [];
    // Combine and deduplicate all ICD-10 codes
    return [...new Set([...rpmCodes, ...ccmCodes])];
  }, [patient]);

  // Get denied codes with descriptions
  const deniedCodesWithDesc = useMemo(() => {
    if (!deniedCodes || deniedCodes.length === 0) {
      const billings = programType === "RPM" ? patient?.rpm_billings : patient?.ccm_billings;
      if (billings) {
        return billings
          .filter(b => b.status === "denied")
          .map(b => ({
            code: b.code,
            description: CPT_DESCRIPTIONS[b.code] || "Service description",
          }));
      }
      return [];
    }
    return deniedCodes.map(code => ({
      code,
      description: CPT_DESCRIPTIONS[code] || "Service description",
    }));
  }, [deniedCodes, patient, programType]);

  // Generate letter body based on all inputs
  const generateLetterBody = () => {
    const deniedCodesList = deniedCodesWithDesc.map(c => `• ${c.code}: ${c.description}`).join('\n');
    const icdCodesList = icdCodes.map(code => `• ${code}`).join('\n') || '• [ICD-10 codes]';

    return `I am writing to appeal the denial of the following CPT code(s) billed for ${patientName}:

${deniedCodesList}

These services were provided as part of the patient's ${programType} program (${programType === "RPM" ? "Remote Physiologic Monitoring" : "Chronic Care Management"}) for management of the following conditions:
${icdCodesList}

${denialInfo.eobDate ? `The stated denial reason per the EOB dated ${denialInfo.eobDate} is:\n"${denialInfo.denialReason || '[Denial reason]'}"\n\n` : ''}We respectfully disagree with this determination. The enclosed documentation and the summary below demonstrate that all CMS and AMA CPT requirements were met.

Patient Clinical Summary
• Patient: ${patientName}${formatAge(patientAge) !== "—" ? `, ${formatAge(patientAge)} years old` : ''}${patientGender ? `, ${patientGender}` : ''}
• Relevant diagnoses: ${icdCodes.join(', ') || '[ICD-10 codes]'}
• Enrollment date in ${programType}: ${formatUSADateTime(enrollmentDate, false) || '[Enrollment date]'}
• Consent obtained: ${formatUSADateTime(consentDate, false) || '[Consent date]'}

Service Details for Denied Period(s)
${deniedCodesWithDesc.map(c => `• CPT ${c.code}: ${c.description}
  - Period: ${billingPeriod}
  - ${programType === "RPM" ? "Days of readings" : "Time"} satisfied: ${serviceDetails.daysOfReadings || '[Days]'} days / ${serviceDetails.totalTimeMinutes || '[Minutes]'} minutes
  - Medical necessity: ${serviceDetails.medicalNecessity || '[Medical necessity statement]'}`).join('\n\n')}

Overall Care Management
• Comprehensive care plan established: ${careMgmt.carePlanDate || '[Care plan date]'}
  Goals: ${careMgmt.goalsSummary || '[Goals summary]'}
  Interventions: ${careMgmt.interventionsSummary || '[Interventions summary]'}
• Clinical notes supporting services: ${careMgmt.clinicalNotesRange || '[Note date range]'}

These services were medically necessary to manage the patient's chronic conditions, prevent acute events, improve adherence, and reduce hospitalizations/emergency visits, consistent with:
• AMA CPT Codebook guidelines
• CMS Medicare Benefit Policy Manual (${programType === "RPM" ? "Chapter 15 § 220" : "Chapter 13"})

Request
We request reconsideration and reversal of the denial for the listed code(s). Payment should be issued per our participating provider agreement / the patient's benefit plan.

Please contact me at ${providerPhone || '[Phone]'} or ${providerEmail || '[Email]'} if you require any additional information. We expect a written determination within 30 days per your plan policies.

Thank you for your prompt attention.`;
  };

  const [letterBody, setLetterBody] = useState("");

  // Update letter body when inputs change
  useEffect(() => {
    setLetterBody(generateLetterBody());
  }, [patient, programType, billingPeriod, denialInfo, serviceDetails, careMgmt, providerPhone, providerEmail, deniedCodesWithDesc, icdCodes]);

  // Validate all required fields based on the Appeal Letter format document
  const isFormValid = useMemo(() => {
    // Email validation
    const isEmailValid = toEmail.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail);

    // Payer Information - all required
    const isPayerValid =
      payerInfo.payerName.trim() !== "" &&
      payerInfo.payerAddress.trim() !== "" &&
      payerInfo.payerCity.trim() !== "" &&
      payerInfo.payerState.trim() !== "" &&
      payerInfo.payerZip.trim() !== "";

    // Claim Information - memberId and claimNumber required
    const isClaimValid =
      claimInfo.memberId.trim() !== "" &&
      claimInfo.claimNumber.trim() !== "";

    // Denial Information - all required
    const isDenialValid =
      denialInfo.eobDate.trim() !== "" &&
      denialInfo.denialReason.trim() !== "";

    // Service Details - days/minutes and medical necessity required
    const isServiceValid =
      (serviceDetails.daysOfReadings.trim() !== "" || serviceDetails.totalTimeMinutes.trim() !== "") &&
      serviceDetails.medicalNecessity.trim() !== "";

    // Care Management - all required
    const isCareMgmtValid =
      careMgmt.carePlanDate.trim() !== "" &&
      careMgmt.goalsSummary.trim() !== "" &&
      careMgmt.interventionsSummary.trim() !== "" &&
      careMgmt.clinicalNotesRange.trim() !== "";

    // Provider/Signature - provider, title, phone, email required
    const isProviderValid =
      (selectedProviderId !== "" || providerName.trim() !== "") &&
      providerTitle.trim() !== "" &&
      providerPhone.trim() !== "" &&
      providerEmail.trim() !== "";

    return isEmailValid && isPayerValid && isClaimValid && isDenialValid &&
           isServiceValid && isCareMgmtValid && isProviderValid;
  }, [toEmail, payerInfo, claimInfo, denialInfo, serviceDetails, careMgmt,
      selectedProviderId, providerName, providerTitle, providerPhone, providerEmail]);

  const handleSubmit = () => {
    if (!isFormValid) return;

    onSendEmail({
      toEmail,
      letterBody,
      letterDate,
      programType,
      payerInfo,
      claimInfo,
      denialInfo,
      serviceDetails,
      careMgmt,
      signatureData: {
        providerName,
        providerTitle,
        credentials,
        npi,
        practiceName,
        providerPhone,
        providerEmail,
        practiceTaxId,
      },
      patientInfo: {
        patientName,
        patientDOB,
        patientAge,
        patientGender,
        enrollmentDate,
        consentDate,
        icdCodes,
      },
      deniedCodes: deniedCodesWithDesc,
    });
  };

  // Styles
  const fieldRowStyle = {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px 0",
    borderBottom: "1px solid #f3f4f6",
  };

  const labelStyle = {
    width: "130px",
    flexShrink: 0,
    fontSize: "13px",
    fontWeight: 500,
    color: "#6b7280",
    paddingTop: "2px",
  };

  // Required field label component
  const RequiredLabel = ({ children, style = {} }) => (
    <span style={{ ...labelStyle, ...style }}>
      {children}<span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
    </span>
  );

  const valueStyle = {
    flex: 1,
    fontSize: "13px",
    color: "#1f2937",
  };

  const inputStyle = {
    width: "100%",
    border: "none",
    background: "transparent",
    fontSize: "13px",
    color: "#1f2937",
    padding: 0,
    outline: "none",
  };

  const sectionHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    backgroundColor: "#f3f4f6",
    borderRadius: "6px",
    marginTop: "12px",
    marginBottom: "8px",
    cursor: "pointer",
  };

  const getSelectedProviderDisplay = () => {
    if (providerName) return providerName;
    if (selectedProviderId && providers.length > 0) {
      const provider = providers.find(p => p.id === selectedProviderId || p.staff_id === selectedProviderId);
      return provider?.name || provider?.staff_name || "Select a provider";
    }
    return "Select a provider";
  };

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent style={{ width: "900px", maxWidth: "95vw", maxHeight: "90vh" }}>
        <DialogHeader>
          <DialogTitle className={styles.alertTitle}>
            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "10px" }} />
            Appeal Letter
          </DialogTitle>
        </DialogHeader>
        <DialogBody style={{ maxHeight: "calc(90vh - 140px)", overflowY: "auto" }}>
          <div className={styles.modalBody}>
            {/* Date & Edit Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              backgroundColor: "#f9fafb",
              borderRadius: "6px",
              marginBottom: "16px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>Date:</span>
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1f2937" }}>{letterDate}</span>
              </div>
              <button
                onClick={() => setIsEditing(!isEditing)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: isEditing ? "#0f1e59" : "#6b7280",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "12px"
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                {isEditing ? "Done" : "Edit"}
              </button>
            </div>

            {/* Email To */}
            <div style={fieldRowStyle}>
              <RequiredLabel>To (Email):</RequiredLabel>
              <div style={valueStyle}>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  style={inputStyle}
                  placeholder="Enter recipient email address"
                />
              </div>
            </div>

            {/* === PAYER INFORMATION SECTION === */}
            <div style={sectionHeaderStyle} onClick={() => toggleSection('payer')}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Payer Information</span>
              <FontAwesomeIcon icon={sectionsOpen.payer ? faChevronUp : faChevronDown} size="sm" color="#6b7280" />
            </div>
            {sectionsOpen.payer && (
              <div style={{ paddingLeft: "8px" }}>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Payer Name:</RequiredLabel>
                  <input type="text" value={payerInfo.payerName} onChange={(e) => setPayerInfo(p => ({...p, payerName: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Insurance company name" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Payer Address:</RequiredLabel>
                  <input type="text" value={payerInfo.payerAddress} onChange={(e) => setPayerInfo(p => ({...p, payerAddress: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Street address" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>City, State, ZIP:</RequiredLabel>
                  <div style={{ display: "flex", gap: "8px", flex: 1 }}>
                    <input type="text" value={payerInfo.payerCity} onChange={(e) => setPayerInfo(p => ({...p, payerCity: e.target.value}))} style={{...inputStyle, flex: 2}} placeholder="City" />
                    <input type="text" value={payerInfo.payerState} onChange={(e) => setPayerInfo(p => ({...p, payerState: e.target.value}))} style={{...inputStyle, width: "60px"}} placeholder="State" />
                    <input type="text" value={payerInfo.payerZip} onChange={(e) => setPayerInfo(p => ({...p, payerZip: e.target.value}))} style={{...inputStyle, width: "80px"}} placeholder="ZIP" />
                  </div>
                </div>
              </div>
            )}

            {/* === PATIENT & CLAIM INFORMATION SECTION === */}
            <div style={sectionHeaderStyle} onClick={() => toggleSection('patient')}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Patient & Claim Information</span>
              <FontAwesomeIcon icon={sectionsOpen.patient ? faChevronUp : faChevronDown} size="sm" color="#6b7280" />
            </div>
            {sectionsOpen.patient && (
              <div style={{ paddingLeft: "8px" }}>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Patient Name:</span>
                  <span style={valueStyle}>{patientName || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Date of Birth:</span>
                  <span style={valueStyle}>{formatUSADateTime(patientDOB, false) || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Age / Gender:</span>
                  <span style={valueStyle}>{formatAge(patientAge)} years / {patientGender || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Member ID:</RequiredLabel>
                  <input type="text" value={claimInfo.memberId} onChange={(e) => setClaimInfo(c => ({...c, memberId: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Insurance Member ID" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Claim Number:</RequiredLabel>
                  <input type="text" value={claimInfo.claimNumber} onChange={(e) => setClaimInfo(c => ({...c, claimNumber: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Claim number being appealed" />
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Service Period:</span>
                  <span style={valueStyle}>{billingPeriod || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Denied CPT Codes:</span>
                  <div style={valueStyle}>
                    {deniedCodesWithDesc.length > 0 ? deniedCodesWithDesc.map((c, i) => (
                      <div key={i} style={{ fontSize: "12px", color: "#6b7280" }}>{c.code} - {c.description}</div>
                    )) : <span style={{ color: "#9ca3af" }}>No denied codes detected</span>}
                  </div>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>ICD-10 Codes:</span>
                  <span style={valueStyle}>{icdCodes.join(", ") || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Enrollment Date:</span>
                  <span style={valueStyle}>{formatUSADateTime(enrollmentDate, false) || "—"}</span>
                </div>
                <div style={fieldRowStyle}>
                  <span style={labelStyle}>Consent Date:</span>
                  <span style={valueStyle}>{formatUSADateTime(consentDate, false) || "—"}</span>
                </div>
              </div>
            )}

            {/* === DENIAL INFORMATION SECTION === */}
            <div style={sectionHeaderStyle} onClick={() => toggleSection('denial')}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Denial Information</span>
              <FontAwesomeIcon icon={sectionsOpen.denial ? faChevronUp : faChevronDown} size="sm" color="#6b7280" />
            </div>
            {sectionsOpen.denial && (
              <div style={{ paddingLeft: "8px" }}>
                <div style={fieldRowStyle}>
                  <RequiredLabel>EOB Date:</RequiredLabel>
                  <input type="text" value={denialInfo.eobDate} onChange={(e) => setDenialInfo(d => ({...d, eobDate: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Date on EOB (e.g., 01/15/2026)" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Denial Reason:</RequiredLabel>
                  <textarea value={denialInfo.denialReason} onChange={(e) => setDenialInfo(d => ({...d, denialReason: e.target.value}))} style={{...inputStyle, flex: 1, minHeight: "60px", resize: "vertical"}} placeholder="Quote the exact denial reason from the EOB" />
                </div>
              </div>
            )}

            {/* === SERVICE DETAILS SECTION === */}
            <div style={sectionHeaderStyle} onClick={() => toggleSection('service')}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Service Details</span>
              <FontAwesomeIcon icon={sectionsOpen.service ? faChevronUp : faChevronDown} size="sm" color="#6b7280" />
            </div>
            {sectionsOpen.service && (
              <div style={{ paddingLeft: "8px" }}>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Days of Readings:</RequiredLabel>
                  <input type="text" value={serviceDetails.daysOfReadings} onChange={(e) => setServiceDetails(s => ({...s, daysOfReadings: e.target.value}))} style={{...inputStyle, width: "100px"}} placeholder="e.g., 16" />
                  <RequiredLabel style={{ width: "100px", marginLeft: "20px" }}>Total Minutes:</RequiredLabel>
                  <input type="text" value={serviceDetails.totalTimeMinutes} onChange={(e) => setServiceDetails(s => ({...s, totalTimeMinutes: e.target.value}))} style={{...inputStyle, width: "100px"}} placeholder="e.g., 45" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Medical Necessity:</RequiredLabel>
                  <textarea value={serviceDetails.medicalNecessity} onChange={(e) => setServiceDetails(s => ({...s, medicalNecessity: e.target.value}))} style={{...inputStyle, flex: 1, minHeight: "50px", resize: "vertical"}} placeholder="Brief medical necessity statement" />
                </div>
              </div>
            )}

            {/* === CARE MANAGEMENT SECTION === */}
            <div style={sectionHeaderStyle} onClick={() => toggleSection('careMgmt')}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Care Management</span>
              <FontAwesomeIcon icon={sectionsOpen.careMgmt ? faChevronUp : faChevronDown} size="sm" color="#6b7280" />
            </div>
            {sectionsOpen.careMgmt && (
              <div style={{ paddingLeft: "8px" }}>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Care Plan Date:</RequiredLabel>
                  <input type="text" value={careMgmt.carePlanDate} onChange={(e) => setCareMgmt(c => ({...c, carePlanDate: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Date care plan was established" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Goals Summary:</RequiredLabel>
                  <input type="text" value={careMgmt.goalsSummary} onChange={(e) => setCareMgmt(c => ({...c, goalsSummary: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Brief summary of care plan goals" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Interventions:</RequiredLabel>
                  <input type="text" value={careMgmt.interventionsSummary} onChange={(e) => setCareMgmt(c => ({...c, interventionsSummary: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Key interventions summary" />
                </div>
                <div style={fieldRowStyle}>
                  <RequiredLabel>Clinical Notes:</RequiredLabel>
                  <input type="text" value={careMgmt.clinicalNotesRange} onChange={(e) => setCareMgmt(c => ({...c, clinicalNotesRange: e.target.value}))} style={{...inputStyle, flex: 1}} placeholder="Date range of supporting notes" />
                </div>
              </div>
            )}

            {/* === PROVIDER / SIGNATURE SECTION === */}
            <div style={{ ...sectionHeaderStyle, backgroundColor: "#e0f2fe" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#0369a1" }}>Provider / Signature</span>
            </div>
            <div style={{ paddingLeft: "8px" }}>
              <div style={fieldRowStyle}>
                <RequiredLabel>Provider:</RequiredLabel>
                <div style={{ flex: 1 }}>
                  <Select onChange={(val) => setSelectedProviderId(val)} value={selectedProviderId}>
                    <SelectTrigger style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "6px 10px" }}>
                      <SelectValue placeholder="Select a provider">{getSelectedProviderDisplay()}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingProviders ? (
                        <SelectItem value="" disabled>Loading...</SelectItem>
                      ) : (
                        providers.map((provider) => (
                          <SelectItem key={provider.id || provider.staff_id} value={provider.id || provider.staff_id}>
                            {provider.name || provider.staff_name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim()}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div style={fieldRowStyle}>
                <RequiredLabel>Title:</RequiredLabel>
                <input type="text" value={providerTitle} onChange={(e) => setProviderTitle(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="e.g., Physician, Billing Manager" />
                <span style={{ ...labelStyle, width: "80px", marginLeft: "16px" }}>Credentials:</span>
                <input type="text" value={credentials} onChange={(e) => setCredentials(e.target.value)} style={{...inputStyle, width: "100px"}} placeholder="MD, DO, NP" />
              </div>
              <div style={fieldRowStyle}>
                <span style={labelStyle}>NPI:</span>
                <input type="text" value={npi} onChange={(e) => setNpi(e.target.value)} style={{...inputStyle, width: "150px"}} placeholder="Provider NPI" />
                <span style={{ ...labelStyle, width: "80px", marginLeft: "16px" }}>Tax ID:</span>
                <input type="text" value={practiceTaxId} onChange={(e) => setPracticeTaxId(e.target.value)} style={{...inputStyle, width: "150px"}} placeholder="Practice Tax ID" />
              </div>
              <div style={fieldRowStyle}>
                <span style={labelStyle}>Practice Name:</span>
                <input type="text" value={practiceName} onChange={(e) => setPracticeName(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Practice name" />
              </div>
              <div style={fieldRowStyle}>
                <RequiredLabel>Contact Phone:</RequiredLabel>
                <input type="text" value={providerPhone} onChange={(e) => setProviderPhone(e.target.value)} style={{...inputStyle, width: "150px"}} placeholder="Phone number" />
                <RequiredLabel style={{ width: "100px", marginLeft: "16px" }}>Contact Email:</RequiredLabel>
                <input type="text" value={providerEmail} onChange={(e) => setProviderEmail(e.target.value)} style={{...inputStyle, flex: 1}} placeholder="Email address" />
              </div>
            </div>

            {/* Letter Preview */}
            <div style={{ marginTop: "20px", paddingTop: "16px", borderTop: "2px solid #e5e7eb" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>
                Letter Preview
              </div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#0d9488", marginBottom: "8px" }}>
                Dear Appeals Coordinator,
              </div>

              {isEditing ? (
                <textarea
                  value={letterBody}
                  onChange={(e) => setLetterBody(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "300px",
                    padding: "12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    color: "#374151",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
              ) : (
                <div style={{
                  fontSize: "12px",
                  lineHeight: "1.6",
                  color: "#374151",
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#f8fafc",
                  padding: "14px",
                  borderRadius: "8px",
                  maxHeight: "300px",
                  overflowY: "auto"
                }}>
                  {letterBody}
                </div>
              )}

              {/* Signature Display */}
              <div style={{ marginTop: "16px", fontSize: "12px", color: "#374151", lineHeight: "1.5" }}>
                <div style={{ marginBottom: "6px" }}>Sincerely,</div>
                <div style={{ fontWeight: 500 }}>{providerName || "[Provider Name]"}</div>
                <div style={{ color: "#6b7280" }}>{providerTitle || "[Title]"}{credentials ? `, ${credentials}` : ""}</div>
                <div style={{ color: "#6b7280" }}>{practiceName || "[Practice Name]"}</div>
                <div style={{ color: "#6b7280" }}>{npi ? `NPI: ${npi}` : "[NPI]"}{practiceTaxId ? ` | Tax ID: ${practiceTaxId}` : ""}</div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!isFormValid || isSending}
            style={{
              backgroundColor: "#0f1e59",
              color: "white",
              opacity: (!isFormValid || isSending) ? 0.5 : 1
            }}
          >
            {isSending ? (
              <>
                <span style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  borderTopColor: "#ffffff",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  display: "inline-block",
                  marginRight: "8px"
                }} />
                Sending...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: "8px" }} />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppealLetterEmailForm;
